import { useMemo, useRef, useState } from "react";
import { useAppStore } from "../store/useAppStore";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  CheckCircle2,
  FileText,
  Upload,
  FileUp,
  Loader2,
  Sparkles,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Papa from "papaparse";
import { scrubLeadsBatch } from "../lib/gemini";

type ImportLead = {
  sellerName: string;
  phone: string;
  propertyAddress: string;
  askingPrice: number | null;
  motivation: string;
  leadSource: string;
  status: "New Lead";
  notes: string;
  tags: string[];
};

const FIELD_ALIASES: Record<string, string[]> = {
  sellerName: ["name", "seller name", "seller"],
  phone: ["phone", "phone number", "mobile"],
  propertyAddress: ["property address", "address", "property"],
  askingPrice: ["asking price", "price", "asking"],
  motivation: ["motivation", "notes", "reason"],
};

function getCellValue(row: Record<string, unknown>, aliases: string[]) {
  const match = Object.entries(row).find(([key]) =>
    aliases.some((alias) => key.trim().toLowerCase() === alias.toLowerCase()),
  );

  const value = match?.[1];
  if (value === undefined || value === null) return "";
  return String(value).trim();
}

function countRecognizedColumns(headers: string[] | undefined) {
  if (!headers?.length) return 0;
  const normalized = headers.map((header) => header.trim().toLowerCase());
  return Object.values(FIELD_ALIASES).filter((aliases) =>
    aliases.some((alias) => normalized.includes(alias.toLowerCase())),
  ).length;
}

function buildLeadDedupKey(sellerName: string, phone: string, propertyAddress: string) {
  return [sellerName, phone, propertyAddress]
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean)
    .join("|");
}

export default function ImportLeads() {
  const { addLead, leads } = useAppStore();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [importedCount, setImportedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [skippedCount, setSkippedCount] = useState(0);
  const [duplicateCount, setDuplicateCount] = useState(0);
  const [progress, setProgress] = useState(0);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [detectedColumns, setDetectedColumns] = useState(0);
  const [useAI, setUseAI] = useState(false);
  const [importSummary, setImportSummary] = useState<{ imported: number; scanned: number; skippedEmpty: number; skippedDuplicates: number; detectedColumns: number } | null>(null);

  const selectedFileLabel = useMemo(() => {
    if (!selectedFileName) return "No file selected yet";
    return selectedFileName;
  }, [selectedFileName]);

  const startImport = async (file: File) => {
    setIsDone(false);
    setImportSummary(null);
    setIsUploading(true);
    setProgress(8);
    setImportedCount(0);
    setTotalCount(0);
    setSkippedCount(0);
    setDuplicateCount(0);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          const rows = results.data as Record<string, unknown>[];
          const leadsToImport: ImportLead[] = [];
          const skippedKeys = new Set<string>();
          const existingKeys = new Set(
            leads
              .map((lead) => buildLeadDedupKey(lead.sellerName, lead.phone, lead.propertyAddress))
              .filter(Boolean)
          );
          let skippedRows = 0;
          let skippedDuplicates = 0;

          for (const row of rows) {
            const sellerNameRaw = getCellValue(row, FIELD_ALIASES.sellerName);
            const phone = getCellValue(row, FIELD_ALIASES.phone);
            const propertyAddress = getCellValue(row, FIELD_ALIASES.propertyAddress);
            const askingPriceRaw = getCellValue(row, FIELD_ALIASES.askingPrice);
            const motivationRaw = getCellValue(row, FIELD_ALIASES.motivation);

            if (!sellerNameRaw && !phone && !propertyAddress && !askingPriceRaw && !motivationRaw) {
              skippedRows += 1;
              continue;
            }

            const sellerName = sellerNameRaw || "Unknown Seller";
            const motivation = motivationRaw || "Imported from CSV";

            let askingPrice: number | null = null;
            if (askingPriceRaw) {
              const parsed = Number(askingPriceRaw.replace(/[^0-9.-]+/g, ""));
              if (!Number.isNaN(parsed)) askingPrice = parsed;
            }

            const tags: string[] = [];
            if (!phone || sellerName === "Unknown Seller") {
              tags.push("Needs Skiptrace");
            }

            const dedupKey = buildLeadDedupKey(sellerName, phone, propertyAddress);
            if (dedupKey && (existingKeys.has(dedupKey) || skippedKeys.has(dedupKey))) {
              skippedDuplicates += 1;
              continue;
            }

            if (dedupKey) {
              existingKeys.add(dedupKey);
              skippedKeys.add(dedupKey);
            }

            leadsToImport.push({
              sellerName,
              phone,
              propertyAddress,
              askingPrice,
              motivation,
              leadSource: "CSV Import",
              status: "New Lead",
              notes: "",
              tags,
            });
          }

          const totalRows = rows.length;
          setTotalCount(totalRows);
          setSkippedCount(skippedRows);
          setDuplicateCount(skippedDuplicates);
          setDetectedColumns(countRecognizedColumns(results.meta.fields));

          if (leadsToImport.length === 0) {
            setIsUploading(false);
            setProgress(0);
            setImportSummary({
              imported: 0,
              scanned: totalRows,
              skippedEmpty: skippedRows,
              skippedDuplicates,
              detectedColumns: countRecognizedColumns(results.meta.fields),
            });
            toast.error("We couldn't find any new importable lead rows in that CSV.");
            return;
          }

          const importedLeads: ImportLead[] = [];

          if (useAI) {
            toast.loading("Scrubbing and scoring leads with AI...", { id: "ai-scrub" });
            const batchSize = 10;
            const totalBatches = Math.max(1, Math.ceil(leadsToImport.length / batchSize));

            for (let index = 0; index < leadsToImport.length; index += batchSize) {
              const chunk = leadsToImport.slice(index, index + batchSize);
              const scoredChunk = await scrubLeadsBatch(chunk);

              for (const lead of scoredChunk) {
                await addLead(lead);
                importedLeads.push(lead);
              }

              const completedBatches = Math.min(totalBatches, Math.ceil((index + batchSize) / batchSize));
              setProgress(Math.round((completedBatches / totalBatches) * 100));
            }

            toast.dismiss("ai-scrub");
          } else {
            for (let index = 0; index < leadsToImport.length; index += 1) {
              await addLead(leadsToImport[index]);
              importedLeads.push(leadsToImport[index]);
              setProgress(Math.round(((index + 1) / leadsToImport.length) * 100));
            }
          }

          const summary = {
            imported: importedLeads.length,
            scanned: totalRows,
            skippedEmpty: skippedRows,
            skippedDuplicates,
            detectedColumns: countRecognizedColumns(results.meta.fields),
          };

          setImportedCount(importedLeads.length);
          setImportSummary(summary);
          setIsUploading(false);
          setProgress(100);
          setIsDone(true);
          toast.success(
            `Imported ${importedLeads.length} leads from ${file.name}${skippedRows || skippedDuplicates ? ` (${skippedRows} empty, ${skippedDuplicates} duplicates skipped)` : ""}`
          );
        } catch (error) {
          console.error(error);
          toast.dismiss("ai-scrub");
          setIsUploading(false);
          setProgress(0);
          toast.error("Import failed while processing the file.");
        }
      },
      error: (error) => {
        console.error(error);
        setIsUploading(false);
        setProgress(0);
        toast.error("Failed to parse CSV file.");
      },
    });
  };

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSelectedFileName(file.name);
    void startImport(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    setSelectedFileName(file.name);
    void startImport(file);
  };

  return (
    <div className="flex flex-col gap-6 w-full max-w-3xl mx-auto">
      <div className="flex flex-col gap-2 rounded-xl bg-white border border-slate-200 shadow-sm px-4 sm:px-8 py-4 -mx-4 sm:-mx-8 -mt-4 sm:-mt-8 mb-2 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600">
            <Upload className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold">Import Leads</h1>
            <p className="text-sm text-slate-500">Drop in a CSV to map, scrub, and add leads in one guided pass.</p>
          </div>
        </div>
      </div>

      <Card className="rounded-xl overflow-hidden border-slate-200 shadow-sm bg-white p-6 sm:p-8">
        {!isDone ? (
          <div
            className={`flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-6 sm:p-10 text-center transition-colors ${
              isDragging ? "border-blue-500 bg-blue-50" : "border-slate-300 bg-slate-50"
            }`}
            onDragEnter={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setIsDragging(false);
            }}
            onDrop={handleDrop}
          >
            {isUploading ? (
              <div className="w-full max-w-md space-y-5">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-800">Processing {selectedFileLabel}</h3>
                  <p className="mt-2 text-sm text-slate-500">
                    {useAI ? "AI scrub is running, then leads are being created." : "We are mapping columns and creating leads now."}
                  </p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>Import progress</span>
                    <span>{progress}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                    <div className="h-full rounded-full bg-blue-600 transition-all" style={{ width: `${progress}%` }} />
                  </div>
                </div>
                <p className="text-xs text-slate-500">Detected columns: {detectedColumns || "scanning..."}</p>
              </div>
            ) : (
              <div className="w-full max-w-2xl space-y-6">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                  <FileUp className="h-8 w-8" />
                </div>

                <div className="space-y-2">
                  <h3 className="text-lg font-bold text-slate-800">Drop your CSV here</h3>
                  <p className="mx-auto max-w-xl text-sm text-slate-500">
                    We automatically map common name, phone, address, price, and notes columns. Unknown rows get a skiptrace tag so nothing slips through.
                  </p>
                </div>

                <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
                  <div className="relative">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                      onChange={handleUpload}
                    />
                    <Button className="pointer-events-none bg-blue-600 px-6 py-2 font-semibold text-white shadow-sm shadow-blue-200 hover:bg-blue-700">
                      Choose CSV file
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <FileText className="h-4 w-4" />
                    <span>{selectedFileLabel}</span>
                  </div>
                </div>

                <div className="grid gap-3 rounded-lg border border-slate-200 bg-white p-4 text-left sm:grid-cols-3">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Step 1</p>
                    <p className="mt-1 text-sm text-slate-700">Upload a CSV export or lead list.</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Step 2</p>
                    <p className="mt-1 text-sm text-slate-700">Review the import summary and optional AI scrub.</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Step 3</p>
                    <p className="mt-1 text-sm text-slate-700">Jump into the lead inbox once the import finishes.</p>
                  </div>
                </div>

                <label className="flex items-start justify-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    id="ai-scrub"
                    checked={useAI}
                    onChange={(e) => setUseAI(e.target.checked)}
                    disabled={isUploading}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600 disabled:cursor-not-allowed"
                  />
                  <span>
                    <span className="flex items-center gap-2 text-sm font-medium text-slate-800">
                      <Sparkles className="h-4 w-4 text-blue-600" />
                      Bulk AI Scrub & Score
                    </span>
                    <span className="mt-1 block text-xs text-slate-500">Uses your API key to enrich and prioritize the imported leads.</span>
                  </span>
                </label>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-6 p-6 sm:p-12 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-bold text-slate-800">Import complete</h3>
              <p className="text-sm text-slate-500 max-w-xl">
                {importSummary
                  ? `${importSummary.imported} leads were added from ${selectedFileLabel}. ${importSummary.skippedEmpty ? `${importSummary.skippedEmpty} empty row${importSummary.skippedEmpty === 1 ? " was" : "s were"} ignored.` : ""} ${importSummary.skippedDuplicates ? `${importSummary.skippedDuplicates} duplicate row${importSummary.skippedDuplicates === 1 ? " was" : "s were"} skipped.` : ""}`
                  : `${importedCount} leads were added to New Lead. ${skippedCount ? `${skippedCount} empty row${skippedCount === 1 ? " was" : "s were"} ignored.` : ""}`}
              </p>
            </div>

            <div className="grid w-full gap-3 sm:grid-cols-4">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs uppercase tracking-wide text-slate-400">Imported</p>
                <p className="mt-1 text-2xl font-bold text-slate-900">{importSummary?.imported ?? importedCount}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs uppercase tracking-wide text-slate-400">Rows scanned</p>
                <p className="mt-1 text-2xl font-bold text-slate-900">{importSummary?.scanned ?? totalCount}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs uppercase tracking-wide text-slate-400">Empty skipped</p>
                <p className="mt-1 text-2xl font-bold text-slate-900">{importSummary?.skippedEmpty ?? skippedCount}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs uppercase tracking-wide text-slate-400">Duplicates skipped</p>
                <p className="mt-1 text-2xl font-bold text-slate-900">{importSummary?.skippedDuplicates ?? duplicateCount}</p>
              </div>
            </div>

            <div className="flex w-full flex-wrap gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 text-left text-xs text-slate-500">
              <Badge variant="outline" className="bg-white text-slate-600">{importSummary?.detectedColumns ?? detectedColumns} columns matched</Badge>
              <Badge variant="outline" className="bg-white text-slate-600">AI scrub {useAI ? "enabled" : "off"}</Badge>
              <Badge variant="outline" className="bg-white text-slate-600">CSV import ready for follow-up</Badge>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <Button
                variant="outline"
                onClick={() => {
                  setIsDone(false);
                  setProgress(0);
                  setSelectedFileName("");
                  setImportSummary(null);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
              >
                Upload another file
              </Button>
              <Button onClick={() => navigate("/leads")} className="bg-blue-600 hover:bg-blue-700">
                View lead inbox
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
